import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Alert } from '@material-ui/lab';

import { useSendNetSuitePromptMutation } from 'shared/hooks/infoTech/useSendNetSuitePromptMutation';

import { NetSuiteAIReferencesSidebar } from './netsuiteAIReferencesSidebar';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 96px)',
        maxWidth: 1280,
        margin: '0 auto',
        padding: theme.spacing(2),
        boxSizing: 'border-box',
    },
    header: {
        marginBottom: theme.spacing(1),
    },
    body: {
        flex: 1,
        display: 'flex',
        gap: theme.spacing(2),
        minHeight: 0,
    },
    chatColumn: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    history: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    emptyState: {
        margin: 'auto',
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    messageRow: {
        display: 'flex',
        width: '100%',
    },
    messageRowUser: {
        justifyContent: 'flex-end',
    },
    messageRowAssistant: {
        justifyContent: 'flex-start',
    },
    userBubble: {
        maxWidth: '75%',
        padding: theme.spacing(1.25, 1.75),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: 16,
        borderBottomRightRadius: 4,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    assistantCard: {
        maxWidth: '85%',
        padding: theme.spacing(1.5, 2),
        borderRadius: 16,
        borderBottomLeftRadius: 4,
        backgroundColor: theme.palette.background.paper,
    },
    pendingCard: {
        maxWidth: '85%',
        padding: theme.spacing(1.25, 1.75),
        borderRadius: 16,
        borderBottomLeftRadius: 4,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    responseBody: {
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        backgroundColor: theme.palette.background.default,
        overflowWrap: 'break-word',
    },
    metaRow: {
        marginTop: theme.spacing(2),
    },
    sqlToggle: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        userSelect: 'none',
    },
    sqlBlock: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.5),
        backgroundColor: '#2d2d2d',
        color: '#f2ebd1',
        borderRadius: 4,
        fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        overflowX: 'auto',
    },
    sectionHeader: {
        marginTop: theme.spacing(1),
    },
    tableContainer: {
        marginTop: theme.spacing(1),
        maxHeight: 320,
    },
    tableCell: {
        whiteSpace: 'nowrap',
        maxWidth: 320,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    inputForm: {
        marginTop: theme.spacing(2),
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
    },
    inputField: {
        flex: 1,
    },
    submitButton: {
        height: 56,
    },
}));

function formatCellValue(value) {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

function AssistantMessage({ data }) {
    const classes = useStyles();
    const [sqlOpen, setSqlOpen] = useState(false);

    const rows = Array.isArray(data?.rows) ? data.rows : null;

    const rowColumns = useMemo(() => {
        if (!rows || rows.length === 0) {
            return [];
        }
        const firstRow = rows[0];
        if (firstRow !== null && typeof firstRow === 'object' && !Array.isArray(firstRow)) {
            const columnSet = new Set();
            rows.forEach((row) => {
                if (row && typeof row === 'object' && !Array.isArray(row)) {
                    Object.keys(row).forEach((key) => columnSet.add(key));
                }
            });
            return Array.from(columnSet);
        }
        return null;
    }, [rows]);

    const hasServerFailure = data && data.success === false;

    return (
        <Paper elevation={1} className={classes.assistantCard}>
            {data.error && (
                <Alert severity={hasServerFailure ? 'error' : 'warning'} style={{ marginBottom: 12 }}>
                    {data.error}
                </Alert>
            )}

            {data.text && (
                <div className={classes.responseBody} dangerouslySetInnerHTML={{ __html: data.text }} />
            )}

            {rows && (
                <Box mt={2}>
                    <Typography variant="subtitle2" className={classes.sectionHeader}>
                        Rows ({rows.length})
                    </Typography>
                    {rows.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No rows returned.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} className={classes.tableContainer} variant="outlined">
                            <Table size="small" stickyHeader>
                                {rowColumns && rowColumns.length > 0 ? (
                                    <>
                                        <TableHead>
                                            <TableRow>
                                                {rowColumns.map((col) => (
                                                    <TableCell key={col}>{col}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((row, rowIdx) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <TableRow key={rowIdx}>
                                                    {rowColumns.map((col) => (
                                                        <TableCell key={col} className={classes.tableCell}>
                                                            {formatCellValue(row?.[col])}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </>
                                ) : (
                                    <TableBody>
                                        {rows.map((row, rowIdx) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <TableRow key={rowIdx}>
                                                <TableCell className={classes.tableCell}>
                                                    {formatCellValue(row)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            <Box className={classes.metaRow}>
                <Typography variant="body2" color="textSecondary">
                    Remaining free usage:{' '}
                    <strong>
                        {typeof data.remainingFreeUsage === 'number'
                            ? data.remainingFreeUsage.toLocaleString()
                            : 'N/A'}
                    </strong>
                </Typography>
            </Box>

            {data.sql && (
                <Box mt={2}>
                    <Box
                        className={classes.sqlToggle}
                        onClick={() => setSqlOpen((prev) => !prev)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setSqlOpen((prev) => !prev);
                            }
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={sqlOpen ? 'Hide SQL' : 'Show SQL'}
                            aria-expanded={sqlOpen}
                        >
                            {sqlOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography variant="body2">{sqlOpen ? 'Hide SQL' : 'Show SQL'}</Typography>
                    </Box>
                    <Collapse in={sqlOpen} timeout="auto" unmountOnExit>
                        <pre className={classes.sqlBlock}>{data.sql}</pre>
                    </Collapse>
                </Box>
            )}
        </Paper>
    );
}

const TIMEOUT_ERROR_MESSAGE = 'Response has timed out. Please try again.';
const MAX_TIMEOUT_RETRIES = 5;

function NetSuiteAI() {
    const classes = useStyles();
    const [prompt, setPrompt] = useState('');
    const [messages, setMessages] = useState([]);
    const [isRetrying, setIsRetrying] = useState(false);
    const [retryAttempt, setRetryAttempt] = useState(0);
    const historyRef = useRef(null);
    const messageIdRef = useRef(0);
    const retryCountRef = useRef(0);

    const { isPending, mutate } = useSendNetSuitePromptMutation();

    const isBusy = isPending || isRetrying;

    useEffect(() => {
        const node = historyRef.current;
        if (node) {
            node.scrollTop = node.scrollHeight;
        }
    }, [messages, isBusy]);

    const appendAssistantData = (data) => {
        messageIdRef.current += 1;
        setMessages((prev) => [
            ...prev,
            { id: `a-${messageIdRef.current}`, role: 'assistant', data },
        ]);
    };

    const appendAssistantError = (errorMessage) => {
        messageIdRef.current += 1;
        setMessages((prev) => [
            ...prev,
            {
                id: `a-${messageIdRef.current}`,
                role: 'assistant',
                errorMessage,
            },
        ]);
    };

    const sendPrompt = (promptText) => {
        mutate(promptText, {
            onSuccess: (data) => {
                if (
                    data?.error === TIMEOUT_ERROR_MESSAGE &&
                    retryCountRef.current < MAX_TIMEOUT_RETRIES
                ) {
                    retryCountRef.current += 1;
                    setIsRetrying(true);
                    setRetryAttempt(retryCountRef.current);
                    sendPrompt(promptText);
                    return;
                }
                retryCountRef.current = 0;
                setIsRetrying(false);
                setRetryAttempt(0);
                appendAssistantData(data);
            },
            onError: (err) => {
                retryCountRef.current = 0;
                setIsRetrying(false);
                setRetryAttempt(0);
                appendAssistantError(err?.message || 'Failed to send prompt. Please try again.');
            },
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const trimmed = prompt.trim();
        if (!trimmed || isBusy) {
            return;
        }
        messageIdRef.current += 1;
        const userMessage = { id: `u-${messageIdRef.current}`, role: 'user', content: trimmed };
        setMessages((prev) => [...prev, userMessage]);
        setPrompt('');
        retryCountRef.current = 0;
        setRetryAttempt(0);
        sendPrompt(trimmed);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit(event);
        }
    };

    return (
        <div className={classes.container}>
            <Box className={classes.header}>
                <Typography variant="h5">NetSuite AI</Typography>
                <Typography variant="body2" color="textSecondary">
                    Ask a question and the NetSuite LLM will generate a response. Large queries may take a few seconds.
                </Typography>
            </Box>

            <div className={classes.body}>
                <NetSuiteAIReferencesSidebar />

                <div className={classes.chatColumn}>
                    <div className={classes.history} ref={historyRef} id="NetSuiteAI_chat_history">
                        {messages.length === 0 && !isBusy && (
                            <Typography variant="body2" className={classes.emptyState}>
                                No messages yet. Ask the NetSuite LLM something to get started.
                            </Typography>
                        )}

                        {messages.map((message) => {
                            if (message.role === 'user') {
                                return (
                                    <div
                                        key={message.id}
                                        className={`${classes.messageRow} ${classes.messageRowUser}`}
                                    >
                                        <Paper elevation={1} className={classes.userBubble}>
                                            <Typography variant="body2">{message.content}</Typography>
                                        </Paper>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={message.id}
                                    className={`${classes.messageRow} ${classes.messageRowAssistant}`}
                                >
                                    {message.errorMessage ? (
                                        <Alert severity="error" style={{ maxWidth: '85%' }}>
                                            {message.errorMessage}
                                        </Alert>
                                    ) : (
                                        <AssistantMessage data={message.data} />
                                    )}
                                </div>
                            );
                        })}

                        {isBusy && (
                            <div className={`${classes.messageRow} ${classes.messageRowAssistant}`}>
                                <Paper elevation={1} className={classes.pendingCard}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2" color="textSecondary">
                                        {retryAttempt > 0
                                            ? `Response timed out. Retrying... (attempt ${retryAttempt} of ${MAX_TIMEOUT_RETRIES})`
                                            : 'Thinking...'}
                                    </Typography>
                                </Paper>
                            </div>
                        )}
                    </div>

                    <form className={classes.inputForm} onSubmit={handleSubmit}>
                        <TextField
                            id="NetSuiteAI_prompt_input"
                            className={classes.inputField}
                            label="Message NetSuite AI"
                            placeholder="Ask a question... (Shift+Enter for newline)"
                            multiline
                            minRows={1}
                            maxRows={6}
                            variant="outlined"
                            value={prompt}
                            onChange={(event) => setPrompt(event.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isBusy}
                            fullWidth
                        />
                        <Button
                            id="NetSuiteAI_submit_button"
                            type="submit"
                            color="primary"
                            variant="contained"
                            className={classes.submitButton}
                            disabled={isBusy || prompt.trim().length === 0}
                        >
                            Submit
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export { NetSuiteAI };
